import React, { useState, useEffect, useCallback, useRef } from 'react';
import type { TableOfContentsProps } from '../../types/rules';

/**
 * Floating Table of Contents component.
 * Displays a list of section links that appears when the mouse moves near the right edge,
 * highlights the current section based on scroll position, and auto-hides after inactivity.
 */
export function TableOfContents({
  sections,
  onSectionClick,
}: TableOfContentsProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [activeSection, setActiveSection] = useState<string>('');
  const hideTimeoutRef = useRef<number | null>(null);

  // Clear hide timeout on cleanup
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  // Handle pointer movement to show/hide ToC
  const handlePointerMove = useCallback((event: PointerEvent) => {
    const viewportWidth = window.innerWidth;
    const distanceFromLeft = event.clientX;

    // Show when within 100px of left edge (only on desktop)
    if (distanceFromLeft < 100 && viewportWidth >= 1024) {
      setIsVisible(true);

      // Clear existing timeout and set new one
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }

      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
      }, 3000); // Hide after 3 seconds of inactivity
    }
  }, []);

  // Set up pointer move listener
  useEffect(() => {
    window.addEventListener('pointermove', handlePointerMove);
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
    };
  }, [handlePointerMove]);

  // Track active section using IntersectionObserver
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio > 0.5) {
            setActiveSection(entry.target.id);
          }
        });
      },
      {
        rootMargin: '-20% 0px -70% 0px', // Trigger when section is near top of viewport
        threshold: [0, 0.5, 1],
      },
    );

    sections.forEach((section) => {
      const element = document.getElementById(section.id);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      observer.disconnect();
    };
  }, [sections]);

  // Handle section click with smooth scrolling
  const handleSectionClick = useCallback(
    (sectionId: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();

      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });

        // Call optional callback
        if (onSectionClick) {
          onSectionClick(sectionId);
        }
      }
    },
    [onSectionClick],
  );

  return (
    <nav
      className={`fixed left-0 top-20 z-40 transition-opacity duration-300 hidden lg:block ${
        isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      aria-label="Table of Contents"
    >
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 ml-4 max-w-xs">
        <h3 className="text-xs font-semibold text-gray-900 mb-2 uppercase tracking-wide">
          Table of Contents
        </h3>
        <ul className="space-y-1">
          {sections.map((section) => (
            <li key={section.id}>
              <a
                href={`#${section.id}`}
                onClick={handleSectionClick(section.id)}
                className={`block text-xs py-1 px-2 rounded transition-colors ${
                  activeSection === section.id
                    ? 'bg-red-50 text-red font-medium'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                {section.title}
              </a>
            </li>
          ))}
        </ul>
      </div>
    </nav>
  );
}
